<template>
<!--
  Local URL: http://localhost:8080/submit-vecdyn
  Required Group Membership: Uploader, Curator or Admin
  Purpose: This page is for members of the Uploaders group to upload a file for ingestion into the database. Once the 
  file is uploaded, the user will see any errors preventing the file from being ingested. If the file is successful, 
  the user will see a summary of the data as the system sees it. If it looks the way that they expect, they can
  'confirm' the upload which will let a curator know that there is a file ready to be curated.
-->
  <VDBaseTemplate>
    <b-modal
      id="file_working-modal"
      v-model="showFileModal"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Getting File"
      :header-text-variant="'light'">
      Working on file. Please wait...
      <b-button class="mt-3" block @click="showFileModal = !showFileModal">OK</b-button>
    </b-modal>
    <b-modal
      id="api-err-modal"
      v-model="apiErrModalShow"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Error"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'">
      <span v-html="err_msg"></span>
      <b-button class="mt-3" block @click="hideModal">OK</b-button>
    </b-modal>
  
    <!-- This modal shows when a user cancels a file upload -->
    <b-modal
      id="-upload-cancel-modal"
      v-model="uploadCancelModal"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Cancel File Upload"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'">
      <span v-html="err_msg"></span>
      <b-button class="mt-3" block @click="hideModal">Back</b-button>
      <b-button class="mt-3" variant="danger" block @click="cancelFileUpload">Cancel File Upload</b-button>
    </b-modal>
  
    <!-- This modal shows while the file is updated and processed. -->
    <b-modal
      id="warning-modal"
      v-model="workingModalShow"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Processing File"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'">
      <span v-html="'Please wait while the file is processed. This may take several minutes.'"></span>
    </b-modal>
    <div class="w-100 h-100 pb-4">
      <b-container fluid class="pt-5 mt-5">
        <h2 class="tw-mb-0 text-aegis-dk">Submit VecDyn Data</h2>
        <p class="tw-italic tw-text-gray-700">
          Upload VecDyn data for processing.
        </p>
        <!-- It had been my intention to move all of this 'Instructions' code into its own component like it is on the
        VecTraits side. You can't just use that same component because the text is slightly different. For example,
        the required fields are completely different. I suppose I could have created a component and passed an array 
        of required fields to the component as a prop. -->
          <b-button v-b-toggle.collapse-instructions variant="primary">Instructions</b-button>
          <b-collapse id="collapse-instructions">
            <div style="padding:15px;">
              <p><b>Submitting Data</b></p>
              <ol type="1">
                <li>
                  Download template in CSV format by clicking the "Download" button.<br>
                </li>
                <li>
                  You can refer to the
                  <router-link :to="{ name: 'vecdyn-columndefs', params: { } }">
                  <u>field definitions section</u></router-link>
                  to guide you through the data collection and compilation process.
                </li>
                <li>
                  To submit your data, upload your completed file by clicking "Browse" on the form below and then 
                  click "Upload". 
                </li>
                <li>
                  In order to submit data to VecDyn it must first pass validation to check that it is canonical 
                  (as defined by the entries in the
                  <router-link :to="{ name: 'vecdyn-columndefs', params: { } }">
                  <u>field definitions section</u></router-link> ). If there are
                  problems with the file or data, you will see a detailed summary and be given a chance to correct 
                  the errors and upload an amended file.
                </li>
                <li>
                  Once the data passes validation, you will see a summary of the data objects about to be created. 
                  This is to give you a chance to review the operations that
                  are about to be done on the database.
                </li>
                <li>
                  Click the green "Confirm" button to complete the process and write the new data to the database or 
                  click "Cancel" to abort.
                </li>
              </ol>

              <b>Note that the following fields are required and must be filled in for each row:</b><br>
              <ul>
                <li v-for="(definition, index) in reqd_cols" :key="index" :title="definition.definition">{{ definition.field_name }}</li>
              </ul>

              <b>A note about date formats:</b><br>
              <p>The desired date format is YYYY-MM-DD. The system <i>can</i> handle Excel style dates (1/1/00) but 
              some extra care must be taken to prevent errors.
              If only two digits are provided for the year the system will attempt to guess the century by applying 
              the digits to a date in the next or last 50 years.
              For example, if "81" is provided in the data, the system will assign 1981 while "49" will be assigned 
              2049.</p>
              
              <p>Additionally, there could be errors due to ambiguity based on localization. 5/2/2021 could refer to 
                the 5th of February or the 2nd of May depending on where you are.
              The only way to guarantee that your dates will be what you expect them to be is to use the YYYY-MM-DD 
              format.</p>

            </div>
          </b-collapse>
          <b-button variant="primary" class="ml-1" @click="downloadTemplate">Download Template</b-button>
        </b-container>
          <hr />
          <!-- UPLOAD FORM SECTION -->
          <!-- <b-container fluid v-show="!summaryObjectsExist"  style="padding: 50px;"> -->
          <b-container fluid v-show="table_data.length == 0" style="padding: 50px;">
            <b-row>
              <b-col md="6" offset-md="3" class="mt-3">
                <b-form-file
                  id="file"
                  v-model="selectedFile"
                  :state="Boolean(selectedFile)"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  size="lg"
                ></b-form-file>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6" offset-md="3" class="mt-3">
                <div style="float: right; display: inline;">
                  <b-button @click="clearPageState">Cancel</b-button>
                  <b-button @click="onUpload()" variant="primary" class="ml-1">Upload</b-button>
                </div>
              </b-col>
            </b-row>
          </b-container>
          <!-- END UPLOAD FORM SECTION -->
  
        <!-- ERROR SECTION -->
        <b-container fluid style="padding-right: 50px; padding-left: 50px;" v-if="error_list.length > 0" >
          <h3>Errors:</h3>
          <!-- <VBUploadObjectsTable :tableData="error_list" :objectName="'Error'" :type="'error'"/> -->
          <VBUploadObjectsContinuousTable :tableData="error_list" :objectName="'Error'" :type="'error'"/>
          <b-row style="margin-bottom: 1rem;">
            <b-col>
              <div style="float: right; display: inline;">
                <b-button @click="clearPageState()">Cancel</b-button>
              </div>
            </b-col>
          </b-row>
        </b-container>
  
        <!-- SUMMARY SECTION -->
        <b-container fluid style="padding-right: 50px; padding-left: 50px; margin-bottom: 2rem;" v-if="show_summary">
          <h3>Summary:</h3>
          <b-row><b-col><b>Total CSV rows:</b> {{ summary.csv_rows }}</b-col></b-row>
          <b-row><b-col><b>New rows:</b> {{ summary.new_rows }}</b-col></b-row>
          <b-row><b-col><b>New rows in existing datasets:</b> {{ summary.new_rows_existing_datasets }}</b-col></b-row>
          <b-row><b-col><b>Rows already in database:</b> {{ summary.existing_rows }}</b-col></b-row>
          <b-row v-if="summary.duplicate_rows > 0" style="color: red;"><b-col><b>Duplicate rows found in file:</b> {{ summary.duplicate_rows }}</b-col></b-row>
          <b-row><b-col><b>Total Datasets in CSV:</b> {{ summary.csv_datasets }}</b-col></b-row>
          <!-- <b-row><b-col><b>New datasets in CSV:</b> {{ summary.new_datasets.join(', ') }}</b-col></b-row> -->
          <b-row><b-col><b>New datasets in CSV:</b> {{ summary.new_dataset_count }}</b-col></b-row>
            <span v-if="summary.unknown_columns.length">
              <b-row style="color: red;">
                <b-col>
                  <b>Unrecognized columns in the file that will not be processed:</b> <br>
                  {{ summary.unknown_columns.join(', ') }}
                </b-col>
              </b-row>
          </span>
          <b-row>
            <!-- <b-col>
              <h3 style="position: absolute; bottom: 0px; cursor: pointer;">Row Data Summary:</h3>
            </b-col>
            <b-col>
              <b-row>
                  <b-col>
                    <div  style="float: right;">
                      New Row: 
                      <div class="color-box" style="background-color: #c3e6cb; border-color: #8fd19e;"></div>
                    </div>
                  </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <div  style="float: right;">
                    New Row in Exising Dataset: 
                    <div class="color-box" style="background-color: #ffeeba; border-color: #ffdf7e;"></div>
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <div  style="float: right;">
                    Row Already in Database: 
                    <div class="color-box" style="background-color: #f1b0b7; border-color: #8fd19e;"></div>
                  </div>
                </b-col>
              </b-row>
            </b-col> -->
            <!-- <VBUploadObjectsContinuousTable :tableData="cptItems" :objectName="'Row Data Summary'" :type="'summary'"/> -->
            <VBUploadObjectsContinuousTable :tableData="table_data" :fields="fields" :objectName="'Row Data Summary'" :type="'summary'"/>
          </b-row>
          <b-row>
            <b-col>
              <span v-if = "warning_list.length > 0">
                <h3>Warnings:</h3>
                <VBUploadObjectsContinuousTable :tableData="warning_list" :objectName="'Warning'" :type="'warning'"/>
              </span>
              <!-- <div style="margin-bottom: 1rem; width:100%;">
                <h3 style="cursor: pointer;" @click="showObjectSummary = !showObjectSummary">
                  Object Summary:
                  <span v-if="showObjectSummary"> [-] </span>
                  <span v-else> [+] </span>
                </h3>
                <span v-if="showObjectSummary">
                  <VBUploadObjectsContinuousTable :tableData="publicationinfo_objs" :objectName="'Publication Info'" :type="'summary'"/>
                  <VBUploadObjectsContinuousTable :tableData="studymetadata_objs" :objectName="'Study Metadata'" :type="'summary'"/>
                  <VBUploadObjectsContinuousTable :tableData="taxonomy_objs" :objectName="'Taxonomy'" :type="'summary'"/>
                  <VBUploadObjectsContinuousTable :tableData="timeseriesdata_objs" :objectName="'Time Series Data'" :type="'summary'"/>
                  <div style="margin:5px;" v-if="!summaryObjectsExist" >
                    <h4>Uploaded file contains no new objects.</h4>
                  </div>
                </span>
              </div> -->
            </b-col>
          </b-row>
          <b-row style="margin-bottom: 1rem;">
            <b-col>
              <div style="float: right; display: inline;" class="mb-4">
                <b-button @click="onCancel">Cancel</b-button>
                <b-button @click="onConfirm()" variant="success" class="ml-1" :disabled='submitDisabled'>Confirm</b-button>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </VDBaseTemplate>
  </template>
  
  <script>
  // @ is an alias to /src
  import { HTTP } from "@/utilities/http-common";
  import HandleErrors from "@/utilities/handle-errors";
  import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";
  // import VBUploadObjectsTable from "@/components/tables/VBUploadObjectsTable.vue";
  import VBUploadObjectsContinuousTable from "@/components/tables/VBUploadObjectsContinuousTable.vue";
  import { VECDYN_FILE_UPLOAD_DATA_TABLE_FIELDS } from "@/utilities/vecdyn-field-definitions";
  import _ from "lodash";
  export default {
    name: "submit-vecdyn",
    mixins: [VDBaseTemplate],
    components: {
      VDBaseTemplate,
      // VBUploadObjectsTable,
      VBUploadObjectsContinuousTable
    },
    data(){
      return{
        data: [],
        table_data: [],
        // fields: [], // dynamically populated by buildFieldsArray()
        summary: {
          new_dataset_count: 0,
          unknown_columns: [],
        },
        show_summary: false,
        selectedFile: null,
        publicationinfo_objs: [],
        studymetadata_objs: [],
        taxonomy_objs: [],
        timeseriesdata_objs: [],
        uploaded_file_id: '',
        error_list: [],
        warning_list: [],
        reqd_cols: [],
        err_msg: '',
        apiErrModalShow: false,
        uploadCancelModal: false,
        workingModalShow: false,
        showObjectSummary: false,
        showFileModal: false,
        submitDisabled: false,
        // IMPORTANT: Field definitions are imported from a shared utility file
        // to maintain consistency across VecDyn components. 
        // 
        // To add/modify/remove fields:
        // 1. Edit src/utilities/vecdyn-field-definitions.js
        // 2. The changes will automatically apply to both:
        //    - SubmitVecdynData.vue (this file)
        //    - VecDynFileUploadDetail.vue
        // 
        // This ensures both components always have identical field definitions
        // and prevents maintenance issues from having duplicate field lists.
        fields: VECDYN_FILE_UPLOAD_DATA_TABLE_FIELDS
      };
    },
    computed: {
      summaryObjectsExist: function () {
        // return true if there are any elements in any of the object lists
        let summaries = [
          this.publicationinfo_objs,
          this.studymetadata_objs,
          this.taxonomy_objs,
          this.timeseriesdata_objs,
        ]
        return summaries.some((array) => array.length != 0);
      },
      // cptItems(){
      //   // add formatting to the row items based on the value in the RowUploadStatus column
      //   return this.table_data.map((item)=>{
      //     let tmp=item;
      //     if (item.RowUploadStatus == "New row"){
      //       tmp._rowVariant='success';
      //     } else if (item.RowUploadStatus == "New row in existing dataset"){
      //       tmp._rowVariant='warning';
      //     } else if (item.RowUploadStatus == "Row already exists"){
      //       tmp._rowVariant='danger';
      //     }
      //     return tmp;
      //   })
      // },
    },
    watch: {},
    created() {
      document.title = "Submit VecDyn Data";
      this.getColumnDefinitionData();
    },
    methods: {
      hideModal() {
        this.apiErrModalShow = false;
        this.uploadCancelModal = false;
      },
      onFileSelected: function (event) {
        this.selectedFile = event.target.files[0];
      },
      onCancel: function(){
        this.err_msg = "Are you sure you would like to cancel the file upload process?"
        this.uploadCancelModal = true;
      },
      cancelFileUpload: function(){
        HTTP.delete(`/api/vecdyn-file-delete/${this.uploaded_file_id}`)
        .then((response) => {
          this.clearPageState()
          this.uploadCancelModal = false;
          this.$notify({
            text: response.data.message,
            duration: 10000,
            type: "success",
          });
        }).catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          this.workingModalShow = false
        });
      },
      clearPageState: function () {
        this.table_data = [];
        this.fields = [];
        this.summary.new_dataset_count = 0;
        this.summary.unknown_columns = [];
        this.show_summary = false;
        this.showObjectSummary = false;
        this.publicationinfo_objs = [];
        this.studymetadata_objs = [];
        this.taxonomy_objs = [];
        this.timeseriesdata_objs = [];
        this.uploaded_file_id = '';
        this.selectedFile = null;
        this.error_list = [];
        this.warning_list = [];
        this.err_msg = '';
        this.submitDisabled = false;
      },
      onUpload: function () {
        const that = this;
        if (!this.selectedFile) {
            return that.$bvModal.msgBoxOk("Please select a file!", {
            title: "File Error",
            okVariant: "danger",
            centered: true,
          });
        }
        this.workingModalShow = true;
        const fd = new FormData();
        fd.append("file", this.selectedFile, this.selectedFile.name);
        HTTP.post("/api/vecdyn-stage/", fd)
        .then(response => {
          this.table_data = response.data.records;
          this.summary = response.data.summary;
          this.publicationinfo_objs = response.data.publicationinfo_list;
          this.studymetadata_objs = response.data.studymetadata_list;
          this.taxonomy_objs = response.data.taxonomy_list;
          this.timeseriesdata_objs = response.data.timeseriesdata_list;
          this.uploaded_file_id = response.data.file_upload_id;
          this.error_list = response.data.error_list;
          // if there are errors, disable the confirm button
          this.submitDisabled = this.error_list.length > 0;
          this.warning_list = response.data.warning_list;
          this.show_summary = true;
          this.workingModalShow = false
          this.$notify({
            text: "File successfully uploaded!",
            duration: 10000,
            type: "success",
          });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          this.workingModalShow = false
        });
      },
      onConfirm: function () {
        // Inform the backend that the user is happy with the file. The endpoint will do a final check of the file and 
        // then switch the status to "Confirmed" which will let a curator know that the file is ready for ingestion.
        const that = this;
  
        const fd = new FormData();
        fd.append("uploaded_file_id", this.uploaded_file_id);
  
        HTTP.post("/api/vecdyn-confirm/", fd)
        .then(response => { 
          this.err_msg = response.data.err_msg;
          that.selectedFile = null;
          this.$notify({
            text: "File successfully processed!",
            duration: 10000,
            type: "success",
          });
        })
        .catch((error) => { // executes whenever there is a response that is not a 200
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
        this.clearPageState();
      },
      downloadTemplate(){
        // build the params object to be sent with the request.
        var params = new URLSearchParams();
        // params.append(<KEY>, <VALUE>)
  
        var request = {
          params: params,
          responseType: "arraybuffer",
        };
  
        HTTP.get(`/api/vecdyn-template-download/`, request)
        .then((response) => {
          var fileURL = window.URL.createObjectURL(
            new Blob([response.data])
          );
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          var fileName = "vecdyn_template.csv";
          fileLink.setAttribute("download", fileName);
          document.body.appendChild(fileLink);
          fileLink.click();
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
      },
      getColumnDefinitionData() {
        HTTP.get(`/api/column-definitions/`)
        .then(response => {
          // only store vectraits definitions that are required.
          this.reqd_cols = response.data
            .filter(definition => definition.database === "VECDYN" && definition.is_required === true)
            .sort((a, b) => a.field_name.localeCompare(b.field_name));
          })
      },
      downloadCSV: function (items, title) {
        this.showFileModal = true;
        var columns = _.keys(items[0]);
        var csv = columns.join(",") + "\n";
        items.forEach(function (row) {
          csv += _.values(row).join(",");
          csv += "\n";
        });
        this.showFileModal = false;
        var hiddenElement = document.createElement("a");
        hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = title + ".csv";
        hiddenElement.click();
      }
    },
    beforeRouteLeave (to, from , next) {
      if (this.table_data.length > 0) {
        const answer = window.confirm('Do you really want to leave? You have not confirmed your file upload.')
        if (answer) {
          HTTP.delete(`/api/vecdyn-file-delete/${this.uploaded_file_id}`)
          .then(() => {
            this.clearPageState()
            this.uploadCancelModal = false;
          }).catch((error) => {
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
            this.workingModalShow = false
          });
          next()
        } else {
          next(false)
        } 
      }else{
        next()
      }
    },
  };
  </script>
  
  <style scoped>
    .table-head {
      background: #777;
      font-weight: bold;
      text-align: left;
      padding: 5px;
    }
  
    .btn-success {
      background: var(--vb_green)
    }
  </style>